import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Activity,
  AlertTriangle,
  CalendarDays,
  HeartPulse,
  Plus,
  RefreshCcw,
  ShieldAlert,
  Sparkles,
  Zap,
} from 'lucide-react';

import { fitness } from '../../lib/api';

import ActivityTab from './fitness/ActivityTab';
import RecoveryTab from './fitness/RecoveryTab';
import InjuriesTab from './fitness/InjuriesTab';
import { ActivityModal, RecoveryModal, InjuryModal } from './fitness/FitnessModals';
import i18nInstance from '../../i18n';


const t = (key, fallback, options = {}) =>
  i18nInstance.t(key, typeof fallback === 'string' ? { defaultValue: fallback, ...options } : fallback);
const today = () => new Date().toISOString().split('T')[0];

const emptyActivity = () => ({
  activity_type: 'running',
  date: today(),
  duration: 30,
  calories: '',
  distance: '',
  avg_heart_rate: '',
  notes: '',
});

const emptyRecovery = () => ({
  date: today(),
  sleep_hours: '',
  sleep_quality: 3,
  fatigue_level: 3,
  muscle_soreness: 3,
  stress_level: 3,
  notes: '',
});

const emptyInjury = () => ({
  injury_type: '',
  body_part: '',
  severity: 'minor',
  date_occurred: today(),
  treatment: '',
  notes: '',
});

const safeDate = (value) => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? new Date(0) : parsed;
};

const withinDays = (date, days) => {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  return safeDate(date) >= cutoff;
};

const readinessScore = (latestLog) => {
  if (!latestLog) return null;
  const sleep = Math.min((Number(latestLog.sleep_hours) || 0) / 8, 1) * 35;
  const quality = ((Number(latestLog.sleep_quality) || 0) / 5) * 25;
  const fatigue = (1 - ((Number(latestLog.fatigue_level) || 0) - 1) / 4) * 15;
  const soreness = (1 - ((Number(latestLog.muscle_soreness) || 0) - 1) / 4) * 15;
  const stress = (1 - ((Number(latestLog.stress_level) || 0) - 1) / 4) * 10;
  return Math.max(0, Math.min(100, Math.round(sleep + quality + fatigue + soreness + stress)));
};

const FitnessSkeleton = () => (
  <div className="p-4 lg:p-8 mb-20 lg:mb-0" data-testid="fitness-loading">
    <div className="mx-auto max-w-7xl space-y-5">
      <div className="h-56 animate-pulse rounded-lg bg-zinc-200" />
      <div className="grid gap-4 md:grid-cols-4">
        {[0, 1, 2, 3].map((item) => (
          <div key={item} className="h-32 animate-pulse rounded-lg bg-zinc-200" />
        ))}
      </div>
      <div className="h-96 animate-pulse rounded-lg bg-zinc-200" />
    </div>
  </div>
);

const FitnessErrorState = ({ onRetry, t }) => (
  <div className="p-4 lg:p-8 mb-20 lg:mb-0" data-testid="fitness-page">
    <div className="mx-auto max-w-7xl">
      <section className="rounded-lg border border-zinc-200 bg-white p-8 text-center shadow-sm">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-lg bg-rose-50 text-rose-700 ring-1 ring-rose-100">
          <RefreshCcw size={24} strokeWidth={1.8} />
        </div>
        <h1 className="mt-4 text-2xl font-black tracking-normal text-zinc-950">
          {t('fitness.loadError')}
        </h1>
        <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-zinc-500">
          {t('fitness.loadErrorBody')}
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-5 inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-zinc-800 bg-zinc-950 px-4 text-sm font-black text-white transition hover:bg-zinc-800"
        >
          <RefreshCcw size={16} strokeWidth={1.8} />
          {t('common.retry')}
        </button>
      </section>
    </div>
  </div>
);

const FitnessTabs = ({ active, onChange, t }) => {
  const tabs = [
    { id: 'activity', label: t('fitness.tabActivity'), icon: <Activity size={16} strokeWidth={1.8} /> },
    { id: 'recovery', label: t('fitness.tabRecovery'), icon: <HeartPulse size={16} strokeWidth={1.8} /> },
    { id: 'injuries', label: t('fitness.tabInjuries'), icon: <ShieldAlert size={16} strokeWidth={1.8} /> },
  ];

  return (
    <div className="grid gap-2 rounded-lg bg-zinc-100 p-1 sm:grid-cols-3">
      {tabs.map((tab) => {
        const isActive = active === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => onChange(tab.id)}
            data-testid={`fitness-tab-${tab.id}`}
            className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-md px-3 text-sm font-black transition ${
              isActive ? 'bg-white text-zinc-950 shadow-sm' : 'text-zinc-500 hover:text-zinc-900'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        );
      })}
    </div>
  );
};

const StatCard = ({ icon, label, value, detail, tone = 'lime', testId }) => {
  const toneClass =
    tone === 'rose'
      ? 'bg-rose-50 text-rose-700 ring-rose-100'
      : tone === 'blue'
      ? 'bg-blue-50 text-blue-700 ring-blue-100'
      : tone === 'emerald'
      ? 'bg-emerald-50 text-emerald-700 ring-emerald-100'
      : 'bg-lime-50 text-lime-700 ring-lime-100';

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4 shadow-sm" data-testid={testId}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-zinc-500">{label}</p>
          <p className="mt-2 text-3xl font-black tracking-normal text-zinc-950">{value}</p>
        </div>
        <div className={`flex h-11 w-11 items-center justify-center rounded-lg ring-1 ${toneClass}`}>
          {icon}
        </div>
      </div>
      {detail ? <p className="mt-3 text-sm leading-5 text-zinc-500">{detail}</p> : null}
    </div>
  );
};

const FitnessHeroCard = ({ latestActivity, readiness, activeInjuries, weeklyVolume, onAddActivity, onLogRecovery, t, locale }) => (
  <section className="relative overflow-hidden rounded-lg bg-zinc-950 p-5 text-white shadow-sm md:p-7">
    <div className="absolute inset-x-0 top-0 h-px bg-lime-300" />
    <div className="absolute right-0 top-0 h-44 w-44 rounded-full bg-lime-300 opacity-10 blur-3xl" />
    <div className="relative grid gap-6 lg:grid-cols-[1.15fr_0.85fr] lg:items-end">
      <div>
        <div className="inline-flex items-center gap-2 rounded-full border border-lime-300/30 bg-lime-300/10 px-3 py-1 text-xs font-black uppercase tracking-[0.14em] text-lime-200">
          <Sparkles size={14} strokeWidth={1.8} />
          {t('fitness.heroKicker')}
        </div>
        <h1 className="mt-4 text-3xl font-black tracking-normal text-white md:text-4xl">
          {t('fitness.title')}
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-6 text-zinc-300 md:text-base">
          {t('fitness.subtitle')}
        </p>
        <div className="mt-5 flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={onAddActivity}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-lime-300 bg-lime-300 px-4 text-sm font-black text-zinc-950 transition hover:bg-lime-200"
            data-testid="fitness-add-activity"
          >
            <Plus size={16} strokeWidth={1.8} />
            {t('fitness.addActivity')}
          </button>
          <button
            type="button"
            onClick={onLogRecovery}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-white/15 bg-white/10 px-4 text-sm font-black text-white transition hover:bg-white/15"
            data-testid="fitness-log-recovery"
          >
            <HeartPulse size={16} strokeWidth={1.8} />
            {t('fitness.logRecovery')}
          </button>
        </div>
      </div>

      <div className="rounded-lg border border-white/10 bg-white/10 p-4">
        <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-200">
          {t('fitness.lastActivity')}
        </p>
        {latestActivity ? (
          <div className="mt-3">
            <p className="text-2xl font-black tracking-normal text-white">
              {t(`fitness.activityTypes.${latestActivity.activity_type}`, latestActivity.activity_type)}
            </p>
            <p className="mt-1 text-sm text-zinc-300">
              {new Date(latestActivity.date).toLocaleDateString(locale, { day: '2-digit', month: 'short' })} · {latestActivity.duration} {t('common.min')}
            </p>
          </div>
        ) : (
          <p className="mt-3 text-sm leading-6 text-zinc-300">
            {t('fitness.emptyBody')}
          </p>
        )}
        <div className="mt-5 grid grid-cols-3 gap-2 text-center">
          <div className="rounded-lg bg-zinc-950/40 p-3">
            <p className="text-lg font-black text-white">{weeklyVolume}</p>
            <p className="mt-1 text-xs text-zinc-400">{t('common.min')}</p>
          </div>
          <div className="rounded-lg bg-zinc-950/40 p-3">
            <p className="text-lg font-black text-white">{readiness === null ? '-' : readiness}</p>
            <p className="mt-1 text-xs text-zinc-400">{t('fitness.readinessScore')}</p>
          </div>
          <div className="rounded-lg bg-zinc-950/40 p-3">
            <p className="text-lg font-black text-white">{activeInjuries}</p>
            <p className="mt-1 text-xs text-zinc-400">{t('fitness.tabInjuries')}</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const InjuryAlertCard = ({ injuries, t, locale, onAdd }) => {
  const active = injuries.filter((injury) => injury.status !== 'recovered');
  if (active.length === 0) {
    return (
      <section className="rounded-lg border border-emerald-200 bg-emerald-50 p-5 text-emerald-900 shadow-sm">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-white text-emerald-700 ring-1 ring-emerald-100">
            <HeartPulse size={22} strokeWidth={1.8} />
          </div>
          <div>
            <p className="text-sm font-black">{t('fitness.injuryStatus.recovered')}</p>
            <p className="mt-1 text-sm leading-6 opacity-85">
              {t('fitness.noActiveInjuries')}
            </p>
          </div>
        </div>
      </section>
    );
  }

  const latest = active[0];
  return (
    <section className="rounded-lg border border-rose-200 bg-rose-50 p-5 text-rose-900 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-white text-rose-700 ring-1 ring-rose-100">
            <AlertTriangle size={22} strokeWidth={1.8} />
          </div>
          <div>
            <p className="text-sm font-black">{t('fitness.tabInjuries')}</p>
            <p className="mt-1 text-lg font-black">{latest.injury_type}</p>
            <p className="mt-1 text-sm leading-6 opacity-85">
              {latest.body_part} · {new Date(latest.date_occurred).toLocaleDateString(locale)}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex min-h-11 shrink-0 items-center justify-center rounded-lg border border-rose-200 bg-white px-3 text-sm font-black text-rose-700 transition hover:bg-rose-100"
        >
          {t('fitness.logInjury')}
        </button>
      </div>
    </section>
  );
};

const FitnessPage = () => {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState('activity');
  const [activities, setActivities] = useState([]);
  const [recoveryLogs, setRecoveryLogs] = useState([]);
  const [recoveryStats, setRecoveryStats] = useState(null);
  const [injuries, setInjuries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [showInjuryModal, setShowInjuryModal] = useState(false);

  const [newActivity, setNewActivity] = useState(emptyActivity);
  const [newRecovery, setNewRecovery] = useState(emptyRecovery);
  const [newInjury, setNewInjury] = useState(emptyInjury);

  const locale = useMemo(() => {
    if (i18n.language === 'ru') return 'ru-RU';
    if (i18n.language === 'fr') return 'fr-FR';
    return 'en-GB';
  }, [i18n.language]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const [a, r, rs, inj] = await Promise.all([
        fitness.getActivities({}),
        fitness.getRecoveryLogs({}),
        fitness.getRecoveryStats(),
        fitness.getInjuries({}),
      ]);
      setActivities(Array.isArray(a.data) ? a.data : []);
      setRecoveryLogs(Array.isArray(r.data) ? r.data : []);
      setRecoveryStats(rs.data);
      setInjuries(Array.isArray(inj.data) ? inj.data : []);
    } catch (error) {
      console.error('Failed to load fitness data:', error);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const sortedActivities = useMemo(
    () => [...activities].sort((a, b) => safeDate(b.date) - safeDate(a.date)),
    [activities],
  );
  const sortedRecoveryLogs = useMemo(
    () => [...recoveryLogs].sort((a, b) => safeDate(b.date) - safeDate(a.date)),
    [recoveryLogs],
  );
  const sortedInjuries = useMemo(
    () => [...injuries].sort((a, b) => safeDate(b.date_occurred) - safeDate(a.date_occurred)),
    [injuries],
  );
  const weeklyVolume = useMemo(
    () =>
      sortedActivities
        .filter((activity) => withinDays(activity.date, 7))
        .reduce((sum, activity) => sum + (Number(activity.duration) || 0), 0),
    [sortedActivities],
  );
  const latestRecovery = sortedRecoveryLogs[0] || null;
  const readiness = readinessScore(latestRecovery);
  const activeInjuries = sortedInjuries.filter((injury) => injury.status !== 'recovered').length;

  const handleCreateActivity = async (e) => {
    e.preventDefault();
    try {
      await fitness.createActivity({
        ...newActivity,
        date: new Date(newActivity.date).toISOString(),
      });
      setShowActivityModal(false);
      setNewActivity(emptyActivity());
      loadData();
    } catch (error) {
      console.error('Failed to create activity:', error);
    }
  };

  const handleLogRecovery = async (e) => {
    e.preventDefault();
    try {
      await fitness.logRecovery({
        ...newRecovery,
        date: new Date(newRecovery.date).toISOString(),
      });
      setShowRecoveryModal(false);
      setNewRecovery(emptyRecovery());
      loadData();
    } catch (error) {
      console.error('Failed to log recovery:', error);
    }
  };

  const handleLogInjury = async (e) => {
    e.preventDefault();
    try {
      await fitness.logInjury({
        ...newInjury,
        date_occurred: new Date(newInjury.date_occurred).toISOString(),
      });
      setShowInjuryModal(false);
      setNewInjury(emptyInjury());
      loadData();
    } catch (error) {
      console.error('Failed to log injury:', error);
    }
  };

  if (loading) return <FitnessSkeleton />;
  if (loadFailed) return <FitnessErrorState onRetry={loadData} t={t} />;

  return (
    <div className="p-4 lg:p-8 mb-20 lg:mb-0" data-testid="fitness-page">
      <div className="mx-auto max-w-7xl space-y-5">
        <FitnessHeroCard
          latestActivity={sortedActivities[0]}
          readiness={readiness}
          activeInjuries={activeInjuries}
          weeklyVolume={weeklyVolume}
          onAddActivity={() => setShowActivityModal(true)}
          onLogRecovery={() => setShowRecoveryModal(true)}
          t={t}
          locale={locale}
        />

        <div className="grid gap-4 md:grid-cols-4">
          <StatCard
            icon={<Activity size={22} strokeWidth={1.8} />}
            label={t('fitness.totalActivities')}
            value={sortedActivities.length}
            detail={t('fitness.weeklyMinutesDetail', { minutes: weeklyVolume })}
            testId="fitness-stat-total"
          />
          <StatCard
            icon={<Zap size={22} strokeWidth={1.8} />}
            label={t('fitness.lastActivity')}
            value={
              sortedActivities[0]
                ? t(`fitness.activityTypes.${sortedActivities[0].activity_type}`, sortedActivities[0].activity_type)
                : '-'
            }
            tone="blue"
            testId="fitness-stat-last"
          />
          <StatCard
            icon={<HeartPulse size={22} strokeWidth={1.8} />}
            label={t('fitness.readinessScore')}
            value={readiness === null ? '-' : readiness}
            detail={latestRecovery ? t('fitness.recoveryLogs') : t('common.noData')}
            tone="emerald"
          />
          <StatCard
            icon={<ShieldAlert size={22} strokeWidth={1.8} />}
            label={t('fitness.tabInjuries')}
            value={activeInjuries}
            detail={activeInjuries ? t('fitness.injuryStatus.active') : t('fitness.injuryStatus.recovered')}
            tone={activeInjuries ? 'rose' : 'emerald'}
          />
        </div>

        <InjuryAlertCard
          injuries={sortedInjuries}
          t={t}
          locale={locale}
          onAdd={() => setShowInjuryModal(true)}
        />

        <FitnessTabs active={activeTab} onChange={setActiveTab} t={t} />

        {activeTab === 'activity' && (
          <ActivityTab
            activities={sortedActivities}
            t={t}
            locale={locale}
            onAdd={() => setShowActivityModal(true)}
          />
        )}
        {activeTab === 'recovery' && (
          <RecoveryTab
            recoveryStats={recoveryStats}
            recoveryLogs={sortedRecoveryLogs}
            readiness={readiness}
            onLog={() => setShowRecoveryModal(true)}
            t={t}
            locale={locale}
          />
        )}
        {activeTab === 'injuries' && (
          <InjuriesTab
            injuries={sortedInjuries}
            onAdd={() => setShowInjuryModal(true)}
            t={t}
            locale={locale}
          />
        )}
      </div>

      {showActivityModal && (
        <ActivityModal
          draft={newActivity}
          setDraft={setNewActivity}
          onSubmit={handleCreateActivity}
          onClose={() => setShowActivityModal(false)}
          t={t}
        />
      )}
      {showRecoveryModal && (
        <RecoveryModal
          draft={newRecovery}
          setDraft={setNewRecovery}
          onSubmit={handleLogRecovery}
          onClose={() => setShowRecoveryModal(false)}
          t={t}
        />
      )}
      {showInjuryModal && (
        <InjuryModal
          draft={newInjury}
          setDraft={setNewInjury}
          onSubmit={handleLogInjury}
          onClose={() => setShowInjuryModal(false)}
          t={t}
        />
      )}
    </div>
  );
};

export default FitnessPage;
